import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Users, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { labelNivel } from '@/lib/labels';

interface Miembro {
  id: number;
  es_yo: boolean;
  dni: string | null;
  cargo: string;
  orden: number | null;
  nombres: string;
  sexo: string | null;
  edad: number | null;
  es_designado: boolean;
  estado_validacion: string;
  estado_djhv: string;
  estado_documentos: string;
  documentos: {
    total_aplica: number;
    aprobados: number;
    recibidos: number;
    pendientes: number;
  };
}

interface MiLista {
  yo: number;
  lista: {
    id: number;
    n_lista: number;
    region: string;
    provincia: string | null;
    distrito: string | null;
    nivel: string;
    tipo_eleccion: string;
  } | null;
  miembros: Miembro[];
}

const PILL: Record<string, string> = {
  ok: 'bg-green-100 text-green-700',
  observacion: 'bg-amber-100 text-amber-700',
  bloqueante: 'bg-red-100 text-red-700',
  sin_iniciar: 'bg-gray-100 text-gray-600',
  en_proceso: 'bg-amber-100 text-amber-700',
  completa: 'bg-blue-100 text-blue-700',
  confirmada_declara: 'bg-green-100 text-green-700',
  pendiente: 'bg-gray-100 text-gray-600',
  incompleto: 'bg-amber-100 text-amber-700',
  completo: 'bg-blue-100 text-blue-700',
  aprobado: 'bg-green-100 text-green-700',
};

export function MiListaTab() {
  const { data, isLoading, error } = useQuery<MiLista>({
    queryKey: ['mi-lista'],
    queryFn: async () => (await api.get<MiLista>('/me/lista')).data,
  });

  if (isLoading) return <div className="p-6 text-sm text-gray-500">Cargando mi lista…</div>;
  if (error || !data) return <div className="p-6 text-sm text-red-700">No se pudo cargar tu lista</div>;
  if (!data.lista) return <div className="p-6 text-sm text-gray-500">Aún no estás asignado a una lista</div>;

  return (
    <section className="space-y-4">
      <header className="bg-white border rounded p-4">
        <div className="flex items-center gap-2 text-arpp-primary">
          <Users size={20} />
          <h3 className="text-sm font-bold">
            Lista N° {data.lista.n_lista} · {labelNivel(data.lista.tipo_eleccion)}
          </h3>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          {data.lista.region}
          {data.lista.provincia ? ` · ${data.lista.provincia}` : ''}
          {data.lista.distrito ? ` · ${data.lista.distrito}` : ''}
        </p>
      </header>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
        Aquí puedes ver cómo van los demás miembros de tu lista con el llenado
        de su información y la entrega de documentos. <strong>Cada candidato
        completa lo suyo</strong>; tu rol es atender únicamente tu propio expediente.
      </div>

      <div className="bg-white border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Pos</th>
              <th className="px-3 py-2 text-left">Cargo</th>
              <th className="px-3 py-2 text-left">Apellidos y nombres</th>
              <th className="px-3 py-2 text-center">Sexo</th>
              <th className="px-3 py-2 text-right">Edad</th>
              <th className="px-3 py-2 text-center">DJHV</th>
              <th className="px-3 py-2 text-center">Documentos</th>
              <th className="px-3 py-2 text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {data.miembros.map((m) => (
              <tr
                key={m.id}
                className={`border-t ${m.es_yo ? 'bg-blue-50 font-medium' : ''}`}
              >
                <td className="px-3 py-2 text-xs">{m.orden ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{m.cargo}</td>
                <td className="px-3 py-2 text-xs">
                  {m.nombres}
                  {m.es_yo && <span className="ml-2 text-[10px] text-blue-700">← TÚ</span>}
                </td>
                <td className="px-3 py-2 text-center">{m.sexo ?? '—'}</td>
                <td className="px-3 py-2 text-right">{m.edad ?? '—'}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${PILL[m.estado_djhv] ?? ''}`}>
                    {m.estado_djhv}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {m.documentos.total_aplica > 0 ? (
                    <span title={`${m.documentos.aprobados} aprobados · ${m.documentos.recibidos} por revisar · ${m.documentos.pendientes} pendientes`}>
                      <CheckCircle2 size={11} className="inline text-green-600" /> {m.documentos.aprobados}
                      {' · '}
                      <Clock size={11} className="inline text-amber-600" /> {m.documentos.recibidos}
                      {' · '}
                      <AlertCircle size={11} className="inline text-gray-400" /> {m.documentos.pendientes}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${PILL[m.estado_validacion] ?? ''}`}>
                    {m.estado_validacion}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
